/**
 * Avatar Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 343-105
 *
 * A flexible avatar component with support for:
 * - Four sizes: Extra Large, Large, Medium, Small
 * - Two types: Initial (letter) and Image
 * - Two shapes: Square and Rounded
 * - Two modes: Light and Dark
 */

import React from 'react';
import { View, Text, Image, ImageSourcePropType } from 'react-native';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export interface AvatarProps {
  /**
   * Avatar size
   * @default "large"
   */
  size?: 'extraLarge' | 'large' | 'medium' | 'small' | 'extraSmall';

  /**
   * Avatar type
   * @default "initial"
   */
  type?: 'initial' | 'image';

  /**
   * Avatar shape
   * @default "square"
   */
  shape?: 'square' | 'rounded';

  /**
   * Visual mode
   * @default "light"
   */
  mode?: 'light' | 'dark';

  /**
   * Fill treatment for an initials avatar.
   * - `default` — the canvas-coloured chip the Figma frame specifies.
   * - `muted` — a filled grey chip with soft text, for surfaces that repeat
   *   the avatar on every row (the chat feed) where the default reads as
   *   bare letters floating on the page.
   * @default "default"
   */
  tone?: 'default' | 'muted';

  /**
   * Initial letter(s) to display (for type="initial")
   * @default "A"
   */
  text?: string;

  /**
   * Image source (for type="image")
   */
  imageSource?: ImageSourcePropType;

  /**
   * Image URL (alternative to imageSource)
   */
  imageUrl?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  size = 'large',
  type = 'initial',
  shape = 'square',
  mode = 'light',
  tone = 'default',
  text = 'A',
  imageSource,
  imageUrl,
}) => {
  // Box sizes come from the Figma frame; the type ramp comes from the Crop
  // Circle system doc (24/10, 32/12, 40/14, 56/18, 80/24), which sizes the
  // glyph for two-letter initials. The Figma numbers were ~0.85x the box —
  // right for a single letter drawn as artwork, but "RR" at 28px in a 32px
  // chip renders edge-to-edge with no chip left around it.
  const getDimensions = () => {
    switch (size) {
      case 'extraLarge':
        return { size: 48, fontSize: 16 };
      case 'large':
        return { size: 40, fontSize: 14 };
      case 'medium':
        return { size: 32, fontSize: 12 };
      case 'extraSmall':
        return { size: 24, fontSize: 10 };
      case 'small':
      default:
        return { size: 16, fontSize: 8 };
    }
  };

  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (mode === 'dark') {
      return Colors.role.surfaceInverse;
    }
    return tone === 'muted' ? Colors.role.surfaceRaised : Colors.role.canvas;
  };

  // Get text color using Crop Circle color tokens
  const getTextColor = () => {
    if (mode === 'dark') {
      return Colors.role.textOnInverse;
    }
    return tone === 'muted' ? Colors.role.textSoft : Colors.role.textStrong;
  };

  // Get border radius based on shape
  const getBorderRadius = () => {
    if (shape === 'rounded') {
      return 999; // Fully rounded
    }
    return 2; // Square with slight rounding
  };

  const dimensions = getDimensions();
  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();
  const borderRadius = getBorderRadius();

  // Render image avatar
  if (type === 'image' && (imageSource || imageUrl)) {
    return (
      <View
        style={[
          styles.container,
          {
            width: dimensions.size,
            height: dimensions.size,
            borderRadius,
          },
        ]}
      >
        <Image
          source={imageSource || { uri: imageUrl }}
          style={[
            styles.image,
            {
              width: dimensions.size,
              height: dimensions.size,
              borderRadius,
            },
          ]}
          resizeMode="cover"
        />
      </View>
    );
  }

  // Render initial avatar
  return (
    <View
      style={[
        styles.container,
        {
          width: dimensions.size,
          height: dimensions.size,
          backgroundColor,
          borderRadius,
        },
      ]}
    >
      <Text
        style={[
          styles.text,
          {
            color: textColor,
            fontSize: dimensions.fontSize,
            lineHeight: dimensions.fontSize * 1.25,
          },
        ]}
      >
        {text.toUpperCase()}
      </Text>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  text: {
    fontFamily: 'Inter',
    fontWeight: '500',
    textAlign: 'center',
    color: role.textStrong,
  },
  image: {
    width: '100%',
    height: '100%',
  },
}));

Avatar.displayName = 'Avatar';
